import { DateTime } from 'luxon';
// @mui
import Alert from '@mui/material/Alert';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
//
import Iconify from 'src/components/iconify';
import Label from 'src/components/label';
import { IftaFiling, IftaUploadedFile, SOURCE_LABELS } from 'src/models';

// ----------------------------------------------------------------------

// Every import that shaped this filing, with who/when/where-from — the audit
// trail to lean on if an IFTA audit ever asks where the numbers came from.
type FileRow = IftaUploadedFile & { kind: 'Miles' | 'Fuel' };

function formatWhen(uploadedAt: string | null) {
  if (!uploadedAt) return 'unknown';
  return DateTime.fromISO(uploadedAt).toFormat('MMM d, yyyy h:mm a');
}

export default function FilesTab({ filing }: { filing: IftaFiling }) {
  const rows: FileRow[] = [
    ...filing.mileFiles.map((file): FileRow => ({ ...file, kind: 'Miles' })),
    ...filing.fuelFiles.map((file): FileRow => ({ ...file, kind: 'Fuel' })),
  ].sort((a, b) => (b.uploadedAt || '').localeCompare(a.uploadedAt || ''));

  if (rows.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary', py: 4, textAlign: 'center' }}>
        No files yet. Files you upload on the Miles and Fuel steps or tabs show up here.
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      <TableContainer sx={{ width: 'auto', mx: { xs: -2, md: -4 } }} data-testid="files-table">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ pl: { xs: 2, md: 4 } }}>File</TableCell>
            <TableCell>Data</TableCell>
            <TableCell>Integration</TableCell>
            <TableCell>Uploaded</TableCell>
            <TableCell sx={{ pr: { xs: 2, md: 4 } }}>By</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <TableRow key={index}>
              <TableCell sx={{ pl: { xs: 2, md: 4 }, maxWidth: 260 }}>
                {row.fileUrl ? (
                  <Link href={row.fileUrl} target="_blank" rel="noopener" sx={{ color: 'text.primary' }}>
                    {row.fileName}
                  </Link>
                ) : (
                  row.fileName
                )}
              </TableCell>
              <TableCell>
                <Label color={row.kind === 'Miles' ? 'info' : 'success'}>{row.kind}</Label>
              </TableCell>
              <TableCell>{SOURCE_LABELS[row.source] || row.source || 'unknown'}</TableCell>
              <TableCell>{formatWhen(row.uploadedAt)}</TableCell>
              <TableCell sx={{ pr: { xs: 2, md: 4 } }}>{row.uploadedBy || 'unknown'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      </TableContainer>
      <Alert
        severity="success"
        icon={<Iconify icon="solar:shield-check-bold" width={22} />}
        data-testid="files-safe-alert"
      >
        Your files are safe and secure on our servers, ready and protected in case of an IFTA
        audit.
      </Alert>
    </Stack>
  );
}
